import { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import { JournalId, UserId, UserType } from 'types';
import { parseDayjsDateIntoString } from 'utils/parsers/dateTime/parseDayjsDateIntoString';

type SpecialJournalTopicAddPageNetworkManageProps = {
    onAddTopicSuccess: () => void;
    journalId: JournalId;
    userId: UserId;
    userType: UserType;
    initialTopicDate?: string;
};

export const useSpecialJournalTopicAddPageNetworkManage = ({
    onAddTopicSuccess,
    journalId,
    userId,
    userType,
    initialTopicDate,
}: SpecialJournalTopicAddPageNetworkManageProps) => {
    const [selectedFormTopicDate, setSelectedFormTopicDate] = useState<string | undefined>(
        initialTopicDate,
    );

    const handleSelectFormTopicDate = (date: Dayjs | null) => {
        if (date) {
            const parsedDate = parseDayjsDateIntoString(date, 'YYYY-MM-DD');
            setSelectedFormTopicDate(parsedDate);
            return;
        }
        setSelectedFormTopicDate(undefined);
    };

    useEffect(() => {
        if (initialTopicDate) {
            setSelectedFormTopicDate(initialTopicDate);
        }
    }, [initialTopicDate]);

    const filters = {
        date: selectedFormTopicDate || '',
        journalId,
    };

    const {
        data: specialLessonsData,
        isFetching: isSpecialLessonsDataFetching,
        isError: isSpecialLessonsDataError,
    } = useAppQuery(
        'LESSONS_WITHOUT_TOPIC',
        [journalId, selectedFormTopicDate],
        () => StaffHTTPService.employeeSchedule.getLessonsWithoutTopic(filters),
        {
            enabled: !!selectedFormTopicDate,
        },
    );

    const {
        data: employeeData,
        isLoading: isEmployeeDataLoading,
        isError: isEmployeeDataError,
    } = useAppQuery(
        'EMPLOYEE_BY_USER_ID',
        [userId],
        () => StaffHTTPService.employees.getEmployeeByUserId(userId),
        {
            // in future we can allow fetching for OWNER (after adding employee for owner account)
            enabled: userType === 'EMPLOYEE',
        },
    );

    const {
        mutate: addSpecialTopic,
        error: addSpecialTopicError,
        isLoading: isAddSpecialTopicLoading,
    } = useAppMutation(StaffHTTPService.specialTopics.addSpecialTopic, {
        key: ['ADD_SPECIAL_TOPIC'],
        onSuccess: onAddTopicSuccess,
        invalidateQueryKeys: [
            ['SPECIAL_TOPICS', journalId],
            ['LESSONS_WITHOUT_TOPIC', journalId, selectedFormTopicDate],
            ['SPECIAL_ATTENDANCE', journalId],
            ['SPECIAL_DAILY_ATTENDANCE', journalId],
            ['SPECIAL_MONTHLY_ATTENDANCE', journalId],
            ['EMPLOYEE_SCHEDULE'],
            ['UNASSIGNED_CHILDREN'],
        ],
    });

    return {
        addSpecialTopic,
        employeeData,
        addSpecialTopicError,
        isEmployeeDataError,
        isAddSpecialTopicLoading,
        isEmployeeDataLoading,
        specialLessonsData,
        isSpecialLessonsDataFetching,
        isSpecialLessonsDataError,
        handleSelectFormTopicDate,
    };
};
